'use client';

import { Box, Button, Dialog, DialogContent, Drawer } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { useCallback, useState } from 'react';
import { DisclosurePopupTitle } from './DisclosurePopupTitle';

type DisclosurePopupProps = {
  content: React.ReactNode;
  deviceType?: MobileOrDesktop;
  title: React.ReactNode;
  /** Desktop `Dialog` paper max width (e.g. `849px`). Omit to use the default dialog sizing. */
  paperMaxWidth?: string;
};

export function DisclosurePopup({
  title,
  content,
  deviceType = 'desktop',
  paperMaxWidth,
}: DisclosurePopupProps) {
  const [open, setOpen] = useState(false);
  const isMobileAndTabletBreakpoint = useMediaQuery('(max-width:768px)');

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const isMobile = deviceType === 'mobile' || isMobileAndTabletBreakpoint;
  return (
    <>
      <Button
        variant="text"
        onClick={() => setOpen(true)}
        sx={{
          color: 'blues.main',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 325,
          minWidth: 'auto',
          padding: 0,
          textDecoration: 'underline',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'blues.dark',
            textDecoration: 'underline',
            outline: 'none',
          },
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        Read important information here.
      </Button>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          onClose={handleClose}
          open={open}
          slotProps={{ paper: { sx: { borderRadius: '16px', padding: 2, height: '80vh' } } }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 4 }}>
            <DisclosurePopupTitle title={title} handleClose={handleClose} />
            {content}
          </Box>
        </Drawer>
      ) : (
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth={paperMaxWidth != null}
          maxWidth={paperMaxWidth != null ? false : undefined}
          slotProps={{
            paper: {
              sx: {
                p: '1.5rem',
                gap: '1.5rem',
                ...(paperMaxWidth != null ? { maxWidth: paperMaxWidth, width: '100%' } : {}),
              },
            },
          }}
        >
          <DisclosurePopupTitle title={title} handleClose={handleClose} />
          <DialogContent sx={{ p: 0 }}>{content}</DialogContent>
        </Dialog>
      )}
    </>
  );
}
